/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { UPDATE_NEGOTIABLE_QUOTE_TEMPLATE_QUANTITIES_MUTATION } from './graphql/updateNegotiableQuoteTemplateQuantities';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface NegotiableQuoteTemplateItemQuantityInput {
  itemId: string;
  quantity: number;
  minQty?: number;
  maxQty?: number;
}

export interface UpdateQuoteTemplateItemQuantitiesParams {
  templateId: string;
  items: NegotiableQuoteTemplateItemQuantityInput[];
}

export const updateQuoteTemplateItemQuantities = async (
  params: UpdateQuoteTemplateItemQuantitiesParams
) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!params.items || params.items.length === 0) {
    throw new Error('Items array is required and must not be empty');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    const results: any = await fetchGraphQl(
      UPDATE_NEGOTIABLE_QUOTE_TEMPLATE_QUANTITIES_MUTATION,
      {
        variables: {
          input: {
            template_id: params.templateId,
            items: params.items.map((item) => ({
              item_id: item.itemId,
              quantity: item.quantity,
              min_qty: item.minQty,
              max_qty: item.maxQty,
            })),
          },
        },
      }
    );

    if (
      !results?.data?.updateNegotiableQuoteTemplateQuantities?.quote_template
    ) {
      throw new Error('No quote template data received');
    }

    const transformedData = transformQuoteTemplate(
      results.data.updateNegotiableQuoteTemplateQuantities.quote_template
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
